import { useCallback } from 'react';
import { useRouter } from '@/hooks';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { GenerateReport } from '../GenerateReport';
import { abyssColors } from '@/theme/colors';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useGetAssemblies } from '../hooks/useGetAssemblies';
import { VIEW_IN_3D_LABEL, VIEW_IN_3D_TOOLTIP_LABEL } from '@/constants';
import { AssemblyNavigation } from '../AssemblyNavigation';

type ReviewScreenHeaderProps = {
  handleClose: () => void;
};

export const ReviewScreenHeader = ({ handleClose }: ReviewScreenHeaderProps) => {
  const structureId = useGetStructureId();
  const router = useRouter();

  useGetAssemblies();

  const selectedAssemblyName = useAtomValue(state.selectedAssemblyName);

  const handleViewIn3D = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const url = `/analysis/${structureId}/viewer?assembly=${selectedAssemblyName}`;
      const isCtrlClick = event.ctrlKey || event.metaKey;

      isCtrlClick ? window.open(url, '_blank') : router.push(url);
    },
    [router, structureId, selectedAssemblyName]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '2rem 2.4rem',
        borderBottom: `0.1rem solid ${abyssColors.primary[200]}`,
      }}
    >
      <AssemblyNavigation />
      <Stack direction="row" gap="2.4rem" alignItems="center">
        <Stack direction="row" gap="1.6rem" alignItems="center">
          <Tooltip title={VIEW_IN_3D_TOOLTIP_LABEL} placement="bottom" arrow>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AbyssIcon iconName="cube-secondary" size="md" />}
              onClick={handleViewIn3D}
            >
              {VIEW_IN_3D_LABEL}
            </Button>
          </Tooltip>
          <GenerateReport />
        </Stack>
        <IconButton
          size="small"
          onClick={handleClose}
          disableRipple
          sx={{ padding: 0 }}
          aria-label="Close drawer"
        >
          <CloseIcon sx={{ color: abyssColors.primary[700] }} />
        </IconButton>
      </Stack>
    </Box>
  );
};
